import debounce from 'lodash/debounce'
import isEqual from 'lodash/isEqual'
import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'
import useCancelToken from '../../../../_gori/hooks/UseCancelToken'
import {
  BranchesMenuBranch,
  DEFAULT_BRANCHES,
  getSelectedAccountIds,
  mapBranchesResponseToMenuBranches,
} from '../components/BranchesMenu'
import OrganizationService from '../core/_requests'
import {useAuth} from '../../auth/core/Auth'
import {isEnterprise} from '../../auth'

const ASIDE_DEBOUNCE_MS = 1000

const hasSelectionChanged = (
  previousBranches: BranchesMenuBranch[],
  nextBranches: BranchesMenuBranch[]
) => !isEqual(getSelectedAccountIds(previousBranches), getSelectedAccountIds(nextBranches))

type OrganizationBranchesContextModel = {
  branches: BranchesMenuBranch[]
  appliedAccountIds: Array<string | number>
  setBranchesFromAside: (branches: BranchesMenuBranch[]) => void
  applyBranchesFromModal: (branches: BranchesMenuBranch[]) => void
}

const OrganizationBranchesContext = React.createContext<
  OrganizationBranchesContextModel | undefined
>(undefined)

const OrganizationBranchesProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  const {newCancelToken, isCancel} = useCancelToken()
  const [branches, setBranches] = useState<BranchesMenuBranch[]>(DEFAULT_BRANCHES)
  const [appliedAccountIds, setAppliedAccountIds] = useState<Array<string | number>>(() =>
    getSelectedAccountIds(DEFAULT_BRANCHES)
  )
  const {currentUser} = useAuth()
  const branchesRef = useRef(branches)

  branchesRef.current = branches

  const syncAppliedAccountIds = useCallback((nextBranches: BranchesMenuBranch[]) => {
    const nextAccountIds = getSelectedAccountIds(nextBranches)
    setAppliedAccountIds((current) => (isEqual(current, nextAccountIds) ? current : nextAccountIds))
  }, [])

  const applyAccountIdsRef = useRef(syncAppliedAccountIds)
  applyAccountIdsRef.current = syncAppliedAccountIds

  const debouncedApplyAside = useMemo(
    () => debounce(() => applyAccountIdsRef.current(branchesRef.current), ASIDE_DEBOUNCE_MS),
    []
  )

  useEffect(() => () => debouncedApplyAside.cancel(), [debouncedApplyAside])

  const setBranchesFromAside = useCallback(
    (nextBranches: BranchesMenuBranch[]) => {
      const selectionChanged = hasSelectionChanged(branchesRef.current, nextBranches)

      branchesRef.current = nextBranches
      setBranches(nextBranches)

      if (selectionChanged) {
        debouncedApplyAside()
      }
    },
    [debouncedApplyAside]
  )

  const applyBranchesFromModal = useCallback(
    (nextBranches: BranchesMenuBranch[]) => {
      debouncedApplyAside.cancel()

      branchesRef.current = nextBranches
      setBranches(nextBranches)
      syncAppliedAccountIds(nextBranches)
    },
    [debouncedApplyAside, syncAppliedAccountIds]
  )

  useEffect(() => {
    const loadAccountBranches = async () => {
      try {
        if (!currentUser || !isEnterprise(currentUser)) {
          return
        }
        const response = await OrganizationService.getAccountBranches({
          cancelToken: newCancelToken(),
        })
        const nextBranches = mapBranchesResponseToMenuBranches(response)
        if (!nextBranches.length) {
          return
        }

        branchesRef.current = nextBranches
        setBranches(nextBranches)
        syncAppliedAccountIds(nextBranches)
      } catch (error) {
        if (isCancel(error)) return
      }
    }

    loadAccountBranches()
  }, [currentUser, isCancel, newCancelToken, syncAppliedAccountIds])

  const contextValue = useMemo(
    () => ({
      branches,
      appliedAccountIds,
      setBranchesFromAside,
      applyBranchesFromModal,
    }),
    [branches, appliedAccountIds, setBranchesFromAside, applyBranchesFromModal]
  )

  return (
    <OrganizationBranchesContext.Provider value={contextValue}>
      {children}
    </OrganizationBranchesContext.Provider>
  )
}

const useOrganizationBranches = () => {
  const context = useContext(OrganizationBranchesContext)
  if (!context) {
    throw new Error('useOrganizationBranches must be used within OrganizationBranchesProvider')
  }
  return context
}

export {OrganizationBranchesProvider, useOrganizationBranches}
